import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Span } from '@opentelemetry/api';
import { EMPTY, Observable, concat, from, of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  map,
  mergeMap,
  skip,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
} from 'rxjs/operators';

import {
  NotificationItemType,
  itemTypeToDetailType,
  itemTypeToPageRoute,
} from '@ninety/platform/models/notifications/notification-item-type';
import {
  DetailViewActions,
  FeatureFlagKeys,
  NotificationActions,
  SPAN_CONTROL,
  TeamListStateActions,
  TeamSelectors,
  selectCompanyId,
  selectCurrentUserId,
  selectDetailIsOpen,
  selectFeatureFlag,
  selectUserEntities,
  selectUserNamesById,
} from '@ninety/platform/state';
import { NotificationFeedActions } from '@ninety/web/pages/notification-feed/_state/notification-feed.actions';
import { FilterBarActions } from '@ninety/web/pages/shared/layouts';

import { notificationIdFromInboxId } from '../models/inbox-item.mapper';
import { INBOX_ALERT_TYPE, InboxItem, InboxLinkedItem, isUnread } from '../models/inbox-item.model';
import {
  OwnerResolver,
  headlineToLinkedItem,
  issueToLinkedItem,
  milestoneToLinkedItem,
  rockToLinkedItem,
  todoToLinkedItem,
} from '../models/inbox-linked-item.mapper';
import { inboxCountPoll } from '../services/inbox-poll';
import { InboxService } from '../services/inbox.service';

import { InboxEntryPoint } from './inbox-entry-point';
import { InboxActions } from './inbox.actions';
import { InboxSelectors } from './inbox.selectors';

/** How long a linked item waits on a team switch before opening anyway. */
const TEAM_SETTLE_TIMEOUT_MS = 5_000;

/** See `autoFillFilteredView$`: rounds, not requests. One round is up to three requests. */
const AUTO_FILL_MAX_ROUNDS = 5;
/** Long enough for a fan-out's three pages to land, short enough not to be felt. */
const AUTO_FILL_SETTLE_MS = 150;

@Injectable()
export class InboxEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly service = inject(InboxService);
  private readonly document = inject(DOCUMENT);
  private readonly spanControl = inject(SPAN_CONTROL);

  /** Undefined when tracing is off — the `SPAN_CONTROL` default is a no-op. */
  private pageSpan: Span | undefined;
  /** Kept so a company switch can reopen the span with the surface the page was opened from. */
  private pageSpanEntryPoint: InboxEntryPoint | undefined;

  /**
   * Gated on the stream's own flag rather than left off: with `web-inbox-alerts` off this costs
   * nothing, and the server refuses the route anyway. Note it stays an empty list until something
   * calls `POST /inbox/alerts` — no emitter does yet, so this is wiring ahead of the data.
   */
  loadAlertsOnOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened, InboxActions.cleared),
      concatLatestFrom(() => this.store.select(selectFeatureFlag(FeatureFlagKeys.inboxAlerts))),
      filter(([, enabled]) => !!enabled),
      map(() => InboxActions.loadAlerts())
    )
  );

  /** Same first-page rule the other two streams follow: a poll must not drop a paged list. */
  loadAlertsOnRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.refreshRequested),
      concatLatestFrom(() => [
        this.store.select(selectFeatureFlag(FeatureFlagKeys.inboxAlerts)),
        this.store.select(InboxSelectors.selectAlertsAtFirstPage),
      ]),
      filter(([, enabled, atFirstPage]) => !!enabled && atFirstPage),
      map(() => InboxActions.loadAlerts())
    )
  );

  /** Flag-gated for the same reason as alerts: with `web-inbox-messages` off the server refuses
   * the route, and the failure is toasted. `cleared` empties the slice on a company switch without
   * the page unmounting, so it refills here rather than waiting on the next poll tick. */
  loadConversationsOnOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened, InboxActions.cleared),
      concatLatestFrom(() => this.store.select(selectFeatureFlag(FeatureFlagKeys.inboxMessages))),
      filter(([, enabled]) => !!enabled),
      map(() => InboxActions.loadConversations())
    )
  );

  /** Skipped once the reader has paged: `loadConversationsSuccess` replaces the list, which would
   * take everything below the fold away mid-scroll. Opening the page again starts over at page 1. */
  loadConversationsOnRefresh$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.refreshRequested),
      concatLatestFrom(() => [
        this.store.select(selectFeatureFlag(FeatureFlagKeys.inboxMessages)),
        this.store.select(InboxSelectors.selectConversationsAtFirstPage),
      ]),
      filter(([, enabled, atFirstPage]) => !!enabled && atFirstPage),
      map(() => InboxActions.loadConversations())
    )
  );

  /** The inbox brings its own filters in column one, so the shared toolbar would be a second set. */
  hideFilterBarOnOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened),
      map(() => FilterBarActions.hide())
    )
  );

  loadCountsOnOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened),
      map(() => InboxActions.loadCounts())
    )
  );

  /**
   * Notifications are the one source the inbox does not own: their entities load on the feed's own
   * `init`, which otherwise only fires at app bootstrap. Without this, arriving at the inbox shows
   * whatever was true when the tab was opened, and anything raised since needs a page reload.
   *
   * On `cleared` for a second reason: the feed's slice survives a company switch untouched, so its
   * `currentPage` would stay where the last company left it and keep `refreshNotificationsOnPoll$`
   * gated off. `init` answers with `setAll` and resets the page, which is exactly what is wanted.
   */
  refreshNotifications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened, InboxActions.cleared),
      map(() => NotificationFeedActions.init())
    )
  );

  /** Same rule as the conversations refresh, and for a sharper reason: the feed's own reducer
   * answers `init` with `setAll` and resets `currentPage` to 1, so a tick mid-scroll would discard
   * every page the inbox had loaded. */
  refreshNotificationsOnPoll$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.refreshRequested),
      concatLatestFrom(() => this.store.select(InboxSelectors.selectNotificationsAtFirstPage)),
      filter(([, atFirstPage]) => atFirstPage),
      map(() => NotificationFeedActions.init())
    )
  );

  loadAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.loadAlerts),
      switchMap(() =>
        this.service.getAlerts().pipe(
          map(response => InboxActions.loadAlertsSuccess({ response })),
          catchError((error: unknown) =>
            of(InboxActions.loadAlertsFailure({ error, message: 'Could not load alerts.' }))
          )
        )
      )
    )
  );

  loadConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.loadConversations),
      switchMap(() =>
        this.service.getConversations().pipe(
          map(response => InboxActions.loadConversationsSuccess({ response })),
          catchError((error: unknown) =>
            of(InboxActions.loadConversationsFailure({ error, message: 'Could not load conversations.' }))
          )
        )
      )
    )
  );

  /**
   * Fans one "the list wants more" out to whichever streams can answer. Streams that are switched
   * off, exhausted, or already fetching contribute nothing, so a scroll that arrives while a page is
   * in flight is dropped here rather than queued.
   */
  loadMore$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.loadMore),
      concatLatestFrom(() => this.store.select(InboxSelectors.selectLoadMoreTargets)),
      concatMap(([, targets]) =>
        from([
          ...(targets.alerts ? [InboxActions.loadMoreAlerts()] : []),
          ...(targets.conversations ? [InboxActions.loadMoreConversations()] : []),
          ...(targets.notifications ? [NotificationFeedActions.loadMoreNotifications()] : []),
        ])
      )
    )
  );

  /**
   * A view like Milestones slices client-side over whatever pages happen to be loaded, so a short
   * view does not mean an exhausted one. This tops it up until it fills or every stream runs out.
   *
   * The cap matters: `loadMore$` fans out to all three streams, so a round is up to three requests
   * and 125 rows, and a sparse view on a large feed would otherwise walk the whole corpus in.
   *
   * Driven off state rather than off the success actions: "is the view short" is a question about
   * the store, and an action list has to be re-checked by hand every time a stream is added.
   */
  autoFillFilteredView$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened, InboxActions.filterChanged, InboxActions.searchChanged),
      // Each view gets its own budget, and switching away tears down the previous view's fills.
      switchMap(() =>
        this.store.select(InboxSelectors.selectNeedsAutoFill).pipe(
          // One decision per round rather than one per stream — three pages land per fan-out.
          debounceTime(AUTO_FILL_SETTLE_MS),
          filter(Boolean),
          take(AUTO_FILL_MAX_ROUNDS),
          map(() => InboxActions.loadMore())
        )
      )
    )
  );

  loadMoreAlerts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.loadMoreAlerts),
      concatLatestFrom(() => this.store.select(InboxSelectors.selectAlertsPaging)),
      // `exhaustMap`, not `switchMap`: cancelling a page in flight would leave `alertsLoading` true
      // with nothing coming to clear it.
      exhaustMap(([, paging]) =>
        this.service.getAlerts(paging.page + 1).pipe(
          map(response => InboxActions.loadMoreAlertsSuccess({ response })),
          catchError((error: unknown) =>
            of(InboxActions.loadAlertsFailure({ error, message: 'Could not load more alerts.' }))
          )
        )
      )
    )
  );

  loadMoreConversations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.loadMoreConversations),
      concatLatestFrom(() => this.store.select(InboxSelectors.selectConversationsPaging)),
      exhaustMap(([, paging]) =>
        this.service.getConversations(paging.page + 1).pipe(
          map(response => InboxActions.loadMoreConversationsSuccess({ response })),
          catchError((error: unknown) =>
            of(InboxActions.loadConversationsFailure({ error, message: 'Could not load more conversations.' }))
          )
        )
      )
    )
  );

  loadCounts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.loadCounts),
      switchMap(() =>
        this.service.getCounts().pipe(
          map(counts => InboxActions.loadCountsSuccess({ counts })),
          // Swallowed on purpose: this runs on a background poll, so a blip leaves the badge stale
          // for a minute rather than interrupting anyone. The next tick recovers it.
          catchError(() => EMPTY)
        )
      )
    )
  );

  /**
   * The badge's poll. Visibility-gated with idle backoff (see `inboxCountPoll`) and flag-gated:
   * this state is registered app-wide so the badge works off-route, which means without the flag
   * check every user would poll an endpoint their account can't reach.
   */
  pollCounts$ = createEffect(() =>
    this.store.select(selectFeatureFlag(FeatureFlagKeys.inbox)).pipe(
      switchMap(enabled => (enabled ? inboxCountPoll(this.document, window) : EMPTY)),
      map(() => InboxActions.loadCounts())
    )
  );

  /**
   * Refreshes the list itself while the page is on screen. `pollCounts$` only refreshes the badge
   * number, and `/v1/inbox/counts` does not cover notifications at all — their entities load once at
   * app bootstrap, so without this a new row never appears until a reload.
   *
   * Scoped to the open page rather than run app-wide like the badge poll: this is two requests a
   * tick, and only a reader actually looking at the list needs them.
   */
  pollListWhileOpen$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.opened),
      switchMap(() =>
        inboxCountPoll(this.document, window).pipe(
          skip(1),
          map(() => InboxActions.refreshRequested()),
          // Last in the chain: operators after `takeUntil` can resubscribe past the teardown.
          takeUntil(this.actions$.pipe(ofType(InboxActions.closed)))
        )
      )
    )
  );

  markAlertRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.markAlertRead),
      mergeMap(({ alertId }) => this.mutateAlert(this.service.markAlertRead(alertId)))
    )
  );

  resolveAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.resolveAlert),
      mergeMap(({ alertId }) => this.mutateAlert(this.service.resolveAlert(alertId)))
    )
  );

  unresolveAlert$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.unresolveAlert),
      mergeMap(({ alertId }) => this.mutateAlert(this.service.unresolveAlert(alertId)))
    )
  );

  /** Alert mutations change the unread count, so the badge has to be refetched, not inferred. */
  refreshCountsAfterMutation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.alertUpdated, InboxActions.sendMessageSuccess, InboxActions.conversationRead),
      map(() => InboxActions.loadCounts())
    )
  );

  /**
   * Opening a row is what asks for its card. Messages and alerts are their own content, so they
   * have nothing to fetch; any other row whose item can't be read simply renders without a card.
   * Skipped once the item is cached, so reopening the same row costs nothing.
   */
  loadLinkedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.itemClicked),
      concatLatestFrom(({ itemId }) => [
        this.store.select(InboxSelectors.selectItemById(itemId)),
        this.store.select(InboxSelectors.selectLinkedByItemId),
        this.store.select(selectUserNamesById),
        this.store.select(selectUserEntities),
      ]),
      mergeMap(([, item, linked, names, users]) => {
        if (!item || linked[item.itemId]) return EMPTY;

        const resolveOwner: OwnerResolver = (userId, fallbackName) => ({
          userId: userId ?? '',
          name: (userId && names[userId]) || fallbackName || 'Unassigned',
          avatarUrl: userId ? users[userId]?.metadata?.picture?.url : undefined,
        });

        const fetched = this.fetchLinkedItem(item, resolveOwner);
        if (!fetched) return EMPTY;

        // The request is announced before it is made: only the rows that actually fetch put a
        // skeleton in the pane, so an alert or a message never waits on a card it will not get.
        return concat(
          of(InboxActions.loadLinkedItem({ itemId: item.itemId })),
          fetched.pipe(
            map(linkedItem => InboxActions.loadLinkedItemSuccess({ itemId: item.itemId, linked: linkedItem })),
            catchError((error: unknown) =>
              of(
                InboxActions.loadLinkedItemFailure({
                  itemId: item.itemId,
                  error,
                  message: 'Could not load the linked item.',
                })
              )
            )
          )
        );
      })
    )
  );

  /**
   * Reading a notification is what marks it read — the alert and message paths already do this
   * from the detail pane, and the projected feed rows had no equivalent. Read state belongs to the
   * feed, which the inbox only projects, so the mark goes back as the feed's own action; its
   * reducer updates the row the inbox is rendering.
   */
  markNotificationRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.itemClicked),
      concatLatestFrom(() => this.store.select(InboxSelectors.selectAllItems)),
      map(([{ itemId }, items]) => items.find(item => item.id === itemId)),
      filter((item): item is InboxItem => !!item && isUnread(item)),
      map(item => notificationIdFromInboxId(item.id)),
      filter((id): id is number => id !== null),
      map(id => NotificationFeedActions.markNotificationsAsRead({ ids: [id] }))
    )
  );

  /**
   * Every unread row, not just the alerts. The badge counts messages and notifications too, so a
   * mark-all that reached only alerts would leave the number it points at untouched — and alerts
   * are the one source nothing feeds yet, which made the button a no-op.
   *
   * Notifications go in one action because the feed takes a list; the other two have per-item
   * mutations behind them, so they fan out. `markNotificationsAsRead`, not the feed's own
   * `markAllAsRead`, which reads the server's whole feed rather than what the inbox is showing.
   */
  markAllRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.markAllRead),
      concatLatestFrom(() => this.store.select(InboxSelectors.selectAllItems)),
      concatMap(([, items]) => {
        const unread = items.filter(isUnread);
        const notificationIds = unread
          .map(item => notificationIdFromInboxId(item.id))
          .filter((id): id is number => id !== null);

        return from([
          ...unread
            .filter(item => item.itemType === INBOX_ALERT_TYPE)
            .map(item => InboxActions.markAlertRead({ alertId: item.id })),
          ...unread
            .filter((item): item is InboxItem & { threadId: string } => !!item.threadId)
            .map(item => InboxActions.markConversationRead({ conversationId: item.threadId })),
          ...(notificationIds.length
            ? [NotificationFeedActions.markNotificationsAsRead({ ids: notificationIds })]
            : []),
        ]);
      })
    )
  );

  /** Opens the detail over the inbox. `detail` is a sibling outlet of `inbox`, so the panel renders
   * over the list with no primary-outlet navigation.
   *
   * The team still has to settle first: `DetailItemResolver` reads an issue out of team-scoped
   * state, so on the wrong team the panel comes up blank. The switch is invisible here — the inbox
   * hides the filter bar. */
  openLinkedItemInDetail$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.openLinkedItemInDetail),
      concatLatestFrom(() => this.store.select(TeamSelectors.selectFilterBarTeamId)),
      switchMap(([{ item }, teamId]) => {
        const target = this.linkedItemTarget(item);
        if (!target) return EMPTY;

        const opened = DetailViewActions.opened({
          config: { pathParts: [target.detailType, item.itemId] },
          source: 'Inbox Linked Item',
        });

        if (target.isMilestone || teamId === item.teamId) return of(opened);
        return concat(
          of(TeamListStateActions.changeFilterBarTeam({ id: item.teamId })),
          this.teamSettled().pipe(map(() => opened))
        );
      })
    )
  );

  /** Leaves the inbox for the item's page, switching the filter bar to its team on the way. No
   * detail — the page is what was asked for. */
  goToLinkedItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.goToLinkedItem),
      switchMap(({ item }) => {
        const target = this.linkedItemTarget(item);
        if (!target?.pageRoute) return EMPTY;

        return from(this.router.navigate([target.pageRoute])).pipe(
          // Read the team after navigating: the navigation moves the filter bar itself, and asking
          // for the team it already landed on is dropped as a no-op.
          concatLatestFrom(() => this.store.select(TeamSelectors.selectFilterBarTeamId)),
          concatMap(([navigated, teamId]) => {
            // A guard can refuse the page, and moving the filter bar for a page nobody reached is
            // worse than doing nothing.
            if (navigated === false) return EMPTY;
            if (target.isMilestone || teamId === item.teamId) return EMPTY;
            return of(TeamListStateActions.changeFilterBarTeam({ id: item.teamId }));
          })
        );
      })
    )
  );

  /**
   * Moving to another row leaves the shared detail panel showing the previous item, which reads as a
   * stale pane rather than a deliberate one. Also fires on leaving the reading pane entirely, where
   * `itemSelected` carries a null id.
   *
   * Guarded on the outlet actually being open: closing one that is not there navigates away from the
   * current URL before anything else runs, which shows up as a spurious full-page reload.
   */
  closeDetailOnSelectionChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.itemSelected, InboxActions.filterChanged),
      concatLatestFrom(() => this.store.select(selectDetailIsOpen)),
      filter(([, detailIsOpen]) => detailIsOpen),
      map(() => DetailViewActions.close())
    )
  );

  loadMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.loadMessages),
      switchMap(({ conversationId }) =>
        this.service.getConversationMessages(conversationId).pipe(
          map(response => InboxActions.loadMessagesSuccess({ conversationId, response })),
          catchError((error: unknown) =>
            of(InboxActions.loadMessagesFailure({ error, message: 'Could not load messages.' }))
          )
        )
      )
    )
  );

  /** `exhaustMap` for the same reason as the list pages: a cancelled page leaves the spinner stuck. */
  loadMoreMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.loadMoreMessages),
      concatLatestFrom(() => this.store.select(InboxSelectors.selectMessagesPagingByConversation)),
      exhaustMap(([{ conversationId }, pagingByConversation]) =>
        this.service
          .getConversationMessages(conversationId, (pagingByConversation[conversationId]?.page ?? 1) + 1)
          .pipe(
            map(response => InboxActions.loadMoreMessagesSuccess({ conversationId, response })),
            catchError((error: unknown) =>
              of(InboxActions.loadMessagesFailure({ error, message: 'Could not load older messages.' }))
            )
          )
      )
    )
  );

  /** `exhaustMap` so a double-submit can't post the same reply twice.
   *
   * Sending is the inbox's main write, so it gets its own span under the page span. Closed on both
   * branches rather than in `finalize`, which would also fire on unsubscribe and record a success
   * there. Only the body's length is attached, never its text. */
  sendMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.sendMessage),
      exhaustMap(({ conversationId, body }) => {
        const span = this.spanControl.startSpan('InboxSendMessage', {
          attributes: { conversationId, bodyLength: body?.length ?? 0 },
          parentSpan: this.pageSpan,
        });
        return this.service.sendConversationMessage(conversationId, body).pipe(
          map(sent => {
            this.spanControl.closeSpan(span);
            return InboxActions.sendMessageSuccess({ message: sent });
          }),
          catchError((error: unknown) => {
            this.spanControl.closeSpan(span, error instanceof Error ? error : 'Could not send message.');
            return of(InboxActions.sendMessageFailure({ error, message: 'Could not send message.' }));
          })
        );
      })
    )
  );

  markConversationRead$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.markConversationRead),
      mergeMap(({ conversationId }) =>
        this.service.markConversationRead(conversationId).pipe(
          map(() => InboxActions.conversationRead({ conversationId })),
          catchError(() => EMPTY)
        )
      )
    )
  );

  createConversation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.createConversation),
      exhaustMap(({ recipientUserId, body }) =>
        this.service.createConversation(recipientUserId).pipe(
          map(conversation => InboxActions.createConversationSuccess({ conversation, body })),
          catchError((error: unknown) =>
            of(InboxActions.createConversationFailure({ error, message: 'Could not start conversation.' }))
          )
        )
      )
    )
  );

  /** Composing with a body is a send, not just a thread creation. */
  sendComposedMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(InboxActions.createConversationSuccess),
      filter(({ body }) => !!body?.trim()),
      map(({ conversation, body }) => InboxActions.sendMessage({ conversationId: conversation.id, body: body!.trim() }))
    )
  );

  navigateToNewConversation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InboxActions.createConversationSuccess),
        tap(({ conversation }) => this.router.navigate(['/inbox', 'messages', conversation.id]))
      ),
    { dispatch: false }
  );

  /**
   * A message from company A appearing while the user is in company B is a data-leak-shaped bug,
   * so state is dropped on every switch rather than reconciled.
   */
  clearOnCompanySwitch$ = createEffect(() =>
    this.store.select(selectCompanyId).pipe(
      filter(Boolean),
      distinctUntilChanged(),
      skip(1),
      map(() => InboxActions.cleared())
    )
  );

  /**
   * Failures the user is owed an explanation for — everything they asked for by opening the page or
   * touching a row. Two are left out, and only two: the counts poll runs in the background every
   * minute, and a linked item that won't load just leaves the card hidden. Toasting either would
   * interrupt over something the user did not ask for.
   *
   * Both stream failures are listed, and both load effects are flag-gated, so a stream that is
   * switched off never reaches the route and never toasts a 403 the reader can do nothing about.
   */
  notifyOnFailure$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        InboxActions.loadAlertsFailure,
        InboxActions.loadConversationsFailure,
        InboxActions.loadMessagesFailure,
        InboxActions.sendMessageFailure,
        InboxActions.createConversationFailure,
        InboxActions.alertMutationFailed
      ),
      map(({ error, message }) => NotificationActions.notifyError({ error, message }))
    )
  );

  /** Page span: opened on init, closed on destroy, parent of the operation spans below.
   *
   * The active filter is not an attribute on purpose — `opened` is dispatched from the component
   * constructor, before the route-filter `effect()` runs, so the store still holds the previous
   * filter and the value would be wrong on any deep link. */
  openPageSpan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InboxActions.opened),
        concatLatestFrom(() => [this.store.select(selectCurrentUserId), this.store.select(selectCompanyId)]),
        tap(([{ entryPoint }, userId, companyId]) => {
          this.pageSpanEntryPoint = entryPoint;
          this.startPageSpan(userId, companyId);
        })
      ),
    { dispatch: false }
  );

  /** A company switch ends the page session even though the page stays mounted: `companyId` is a
   * span attribute, so one span can't cover both companies.
   *
   * Guarded on an open span, not the action alone — `cleared` comes off a store selector and fires
   * on every switch, inbox on screen or not. */
  recyclePageSpanOnCompanySwitch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InboxActions.cleared),
        filter(() => !!this.pageSpan),
        concatLatestFrom(() => [this.store.select(selectCurrentUserId), this.store.select(selectCompanyId)]),
        tap(([, userId, companyId]) => {
          this.endPageSpan();
          this.startPageSpan(userId, companyId);
        })
      ),
    { dispatch: false }
  );

  closePageSpan$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(InboxActions.closed),
        tap(() => {
          this.endPageSpan();
          this.pageSpanEntryPoint = undefined;
        })
      ),
    { dispatch: false }
  );

  private startPageSpan(userId: string | null | undefined, companyId: string | null | undefined): void {
    this.pageSpan = this.spanControl.startSpan('InboxPage', {
      attributes: {
        entryPoint: this.pageSpanEntryPoint ?? '',
        userId: userId ?? '',
        companyId: companyId ?? '',
      },
    });
  }

  private endPageSpan(): void {
    if (!this.pageSpan) return;
    this.spanControl.closeSpan(this.pageSpan);
    this.pageSpan = undefined;
  }

  /** Where a linked item can be opened. `null` for a row with nothing behind it — a message or an
   * alert. `pageRoute` is separately null when a type has a detail view but nowhere to navigate, so
   * each button checks only what it needs.
   *
   * A milestone has no page of its own; it opens over its rock, which already carries the team. */
  private linkedItemTarget(item: InboxLinkedItem) {
    const itemType = item.itemType as NotificationItemType;
    const detailType = itemTypeToDetailType[itemType];
    if (!detailType) return null;

    const isMilestone = itemType === NotificationItemType.milestone;
    const pageRoute =
      (isMilestone ? item.parent?.itemId && `/rocks/${item.parent.itemId}` : itemTypeToPageRoute[itemType]) || null;

    return { detailType, pageRoute, isMilestone };
  }

  /** Any settled switch releases the detail, not just one onto the item's team — the switch can land
   * elsewhere or fail outright, and waiting for a team that never arrives leaves the reader with no
   * detail at all. A card on the wrong team is visible and recoverable; a blank pane isn't. */
  private teamSettled() {
    return this.actions$.pipe(
      ofType(TeamListStateActions.getSelectedTeamSuccess, TeamListStateActions.getSelectedTeamError),
      take(1),
      timeout({ first: TEAM_SETTLE_TIMEOUT_MS, with: () => of(null) })
    );
  }

  /** One endpoint per type. `null` means the row has no separate item behind it. */
  private fetchLinkedItem(item: InboxItem, resolveOwner: OwnerResolver): Observable<InboxLinkedItem> | null {
    switch (item.itemType) {
      case NotificationItemType.todo:
        return this.service.getTodo(item.itemId).pipe(map(todo => todoToLinkedItem(todo, item, resolveOwner)));
      case NotificationItemType.rock:
        return this.service.getRock(item.itemId).pipe(map(rock => rockToLinkedItem(rock, item, resolveOwner)));
      case NotificationItemType.milestone:
        return this.service
          .getMilestone(item.itemId)
          .pipe(map(milestone => milestoneToLinkedItem(milestone, item, resolveOwner)));
      case NotificationItemType.shortTermIssue:
      case NotificationItemType.longTermIssue:
        return this.service.getIssue(item.itemId).pipe(map(issue => issueToLinkedItem(issue, item, resolveOwner)));
      case NotificationItemType.headline:
      case NotificationItemType.cascadingMessage:
        return this.service
          .getHeadline(item.itemId)
          .pipe(map(headline => headlineToLinkedItem(headline, item, resolveOwner)));
      default:
        return null;
    }
  }

  private mutateAlert(request: ReturnType<InboxService['markAlertRead']>) {
    return request.pipe(
      map(alert => InboxActions.alertUpdated({ alert })),
      catchError((error: unknown) =>
        of(InboxActions.alertMutationFailed({ error, message: 'Could not update alert.' }))
      )
    );
  }
}
